import type { HowItWorksStep, StatItem, Testimonial, TrustFeature } from "@/types/directory";

export const heroStats: StatItem[] = [
  { id: "stat-01", label: "Businesses Listed", value: "4,080+", icon: "ri-store-3-line" },
  { id: "stat-02", label: "Districts Covered", value: "3", icon: "ri-map-pin-2-line" },
  { id: "stat-03", label: "Categories", value: "45+", icon: "ri-apps-2-line" },
  { id: "stat-04", label: "Monthly Visitors", value: "120K+", icon: "ri-group-line" },
];

export const trustFeatures: TrustFeature[] = [
  {
    id: "trust-01",
    title: "Verified Local Businesses",
    description:
      "Every premium listing is checked for a real address, working phone number and valid GST details before it goes live.",
    icon: "ri-shield-check-line",
  },
  {
    id: "trust-02",
    title: "Truly Hyperlocal",
    description:
      "Search by district, town or locality across South Canara, Udupi and North Canara to find businesses right next door.",
    icon: "ri-map-pin-3-line",
  },
  {
    id: "trust-03",
    title: "Direct Contact, No Runaround",
    description:
      "Call, WhatsApp or enquire with one tap. Leads go straight to the business owner the moment they arrive.",
    icon: "ri-chat-smile-3-line",
  },
  {
    id: "trust-04",
    title: "Honest Reviews Only",
    description:
      "Genuine customer ratings with human moderation, so you can trust what you read before you visit.",
    icon: "ri-star-smile-line",
  },
];

export const howItWorksSteps: HowItWorksStep[] = [
  {
    id: "step-01",
    title: "Search the coast",
    description: "Enter what you need and where you are — from Mangaluru to Karwar in seconds.",
    icon: "ri-search-eye-line",
  },
  {
    id: "step-02",
    title: "Compare real businesses",
    description: "Browse verified profiles, photos, reviews, working hours and contact details.",
    icon: "ri-layout-grid-line",
  },
  {
    id: "step-03",
    title: "Connect instantly",
    description: "Call, WhatsApp or send an enquiry directly to the owner — no middlemen.",
    icon: "ri-send-plane-line",
  },
];

export const testimonials: Testimonial[] = [
  {
    id: "testi-01",
    name: "Shrinivas Kamath",
    role: "Owner, Kamath Hardware",
    location: "Udupi",
    quote:
      "We used to depend only on word of mouth. Within a month of listing here we started getting enquiries from across Udupi and Kundapura. The lead dashboard is genuinely useful.",
    rating: 5,
  },
  {
    id: "testi-02",
    name: "Ayesha Rahman",
    role: "Found a dentist in Mangaluru",
    location: "Mangaluru",
    quote:
      "I found a clinic near Kadri with reviews, exact location and open hours in under a minute. Called directly and got an appointment the same day. Simple and trustworthy.",
    rating: 5,
  },
  {
    id: "testi-03",
    name: "Ganesh Pai",
    role: "Manager, Coastal Beach Resort",
    location: "Kundapura",
    quote:
      "The sponsored placement put us in front of travellers actively searching for beach stays in Udupi. Our weekend bookings went up noticeably last season.",
    rating: 5,
  },
  {
    id: "testi-04",
    name: "Deepa Shetty",
    role: "Bridal Studio Owner",
    location: "Karkala",
    quote:
      "Clean profile, gallery and a working WhatsApp button — that combination made all the difference. Brides now reach us directly instead of going through three people.",
    rating: 4,
  },
];

export const popularSearches: string[] = [
  "Pure veg restaurants",
  "Beach resorts",
  "Car service centres",
  "Multispeciality hospitals",
  "Bridal makeup studios",
  "Wedding halls",
  "Two-wheeler dealers",
  "Supermarkets",
];

export interface EmergencyService {
  id: string;
  slug: string;
  label: string;
  icon: string;
  description: string;
  /** Directory category slug this service maps to. Empty string = search all categories. */
  category: string;
  /** Keyword used to narrow the directory results for this service. */
  keyword: string;
}

export interface EmergencyHelpline {
  id: string;
  number: string;
  label: string;
  icon: string;
  note: string;
}

export const emergencyServices: EmergencyService[] = [
  {
    id: "es-01",
    slug: "police-station",
    label: "Police Station",
    icon: "ri-police-car-line",
    description: "Nearest police stations, outposts and control rooms",
    category: "",
    keyword: "Police",
  },
  {
    id: "es-02",
    slug: "fire-rescue",
    label: "Fire & Rescue",
    icon: "ri-fire-line",
    description: "Fire stations, rescue gear and safety equipment dealers",
    category: "",
    keyword: "Fire",
  },
  {
    id: "es-03",
    slug: "hospital",
    label: "Hospital",
    icon: "ri-hospital-line",
    description: "Multispeciality and 24x7 emergency hospitals",
    category: "healthcare",
    keyword: "Hospital",
  },
  {
    id: "es-04",
    slug: "ambulance",
    label: "Ambulance",
    icon: "ri-truck-line",
    description: "Ambulance providers and round-the-clock emergency care",
    category: "healthcare",
    keyword: "Emergency",
  },
  {
    id: "es-05",
    slug: "diagnostic-lab",
    label: "Diagnostic Lab",
    icon: "ri-test-tube-line",
    description: "NABL labs, scan centres and urgent sample collection",
    category: "healthcare",
    keyword: "Diagnostic",
  },
  {
    id: "es-06",
    slug: "district-hospital",
    label: "District Hospital",
    icon: "ri-building-2-line",
    description: "Government and district-level referral hospitals",
    category: "healthcare",
    keyword: "Hospital",
  },
  {
    id: "es-07",
    slug: "blood-bank",
    label: "Blood Bank",
    icon: "ri-heart-pulse-line",
    description: "Blood banks and blood donation centres near you",
    category: "healthcare",
    keyword: "Blood",
  },
  {
    id: "es-08",
    slug: "pharmacy",
    label: "Pharmacy",
    icon: "ri-capsule-line",
    description: "24-hour medical shops with home delivery",
    category: "healthcare",
    keyword: "Pharmacy",
  },
  {
    id: "es-09",
    slug: "nursing-home",
    label: "Nursing Home",
    icon: "ri-home-heart-line",
    description: "Nursing homes, home nursing and post-op care",
    category: "healthcare",
    keyword: "Care",
  },
  {
    id: "es-10",
    slug: "trauma-care",
    label: "Trauma Care",
    icon: "ri-first-aid-kit-line",
    description: "Trauma units and accident & emergency departments",
    category: "healthcare",
    keyword: "Emergency",
  },
];

export const emergencyHelplines: EmergencyHelpline[] = [
  {
    id: "eh-01",
    number: "112",
    label: "All Emergency",
    icon: "ri-alarm-warning-line",
    note: "Single national number for police, fire & medical",
  },
  {
    id: "eh-02",
    number: "108",
    label: "Ambulance",
    icon: "ri-truck-line",
    note: "Free emergency ambulance & medical response",
  },
  {
    id: "eh-03",
    number: "101",
    label: "Fire & Rescue",
    icon: "ri-fire-line",
    note: "State fire and rescue services control room",
  },
  {
    id: "eh-04",
    number: "100",
    label: "Police",
    icon: "ri-police-car-line",
    note: "Police helpline for crime and law enforcement",
  },
];